import { useInquiryModal } from '@/components/inquiry/InquiryModal';
import MetaHead from '@/components/meta-head';
import PublicLayout from '@/layouts/public-layout';
import { motion, useReducedMotion } from 'framer-motion';
import AIFeatureBanner from './journal/AIFeatureBanner';
import ClosingInterstitial from './journal/ClosingInterstitial';
import EditorsPicks from './journal/EditorsPicks';
import HeroSection from './journal/HeroSection';
import JournalNav from './journal/JournalNav';
import SectionCompact from './journal/SectionCompact';
import SectionMagazine from './journal/SectionMagazine';
import SectionStrip from './journal/SectionStrip';
import type { SectionTheme } from './journal/constants';
import { itemVariants, sectionVariants } from './journal/constants';
import type { JournalPageProps } from './journal/types';

const themeForIndex = (i: number): SectionTheme => (i % 2 === 0 ? 'teal' : 'ivory');

const VARIANTS = ['default', 'reversed', 'magazine'] as const;
const variantForIndex = (i: number): (typeof VARIANTS)[number] => VARIANTS[i % 3];

function QuoteInterstitial() {
    const shouldReduceMotion = useReducedMotion();
    const { openInquiry } = useInquiryModal();

    return (
        <motion.section
            className="bg-[#0e2727] py-24 lg:py-32"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={shouldReduceMotion ? undefined : sectionVariants}
        >
            <motion.div className="lux-space-px" variants={shouldReduceMotion ? undefined : itemVariants}>
                <blockquote className="mx-auto max-w-3xl text-center">
                    <svg width="10" height="10" viewBox="0 0 10 10" className="mx-auto mb-8 text-[#F5F5F5]/25">
                        <rect x="5" y="0" width="7.07" height="7.07" rx="0.75" transform="rotate(45 5 0)" fill="currentColor" />
                    </svg>
                    <p className="lux-didot text-[#F5F5F5]/90" style={{ fontSize: 'clamp(1.875rem, 4.5vw, 3.25rem)', lineHeight: 1.15 }}>
                        The world is a book, and those who do not travel read only one page.
                    </p>
                    <footer className="lux-caps lux-caps-sm mt-8 text-[#F5F5F5]/50">Saint Augustine</footer>
                </blockquote>
            </motion.div>
            <motion.div className="mx-auto mt-10 flex justify-center" variants={shouldReduceMotion ? undefined : itemVariants}>
                <button
                    type="button"
                    onClick={() => openInquiry({ source: 'journal_landing', page_url: window.location.href })}
                    className="lux-caps lux-caps-sm cursor-pointer rounded-sm border border-white/30 bg-black/15 px-5 py-2 text-white backdrop-blur-sm transition-colors duration-300 hover:border-white/50 hover:bg-black/25"
                >
                    Start Planning
                </button>
            </motion.div>
        </motion.section>
    );
}

export default function Journal({ hero, trending, latest, editorPicks, sections, featuredAuthor }: JournalPageProps) {
    const topSections = sections.slice(0, 3);
    const bottomSections = sections.slice(3);
    const hasEditorPicks = editorPicks.length > 0;

    return (
        <PublicLayout headerConfig={{ position: 'fixed' }}>
            <MetaHead
                title="Journal | LuxRally Travel"
                description="Luxury travel features, inspiration, guides, and industry insights from the LuxRally editorial team."
            />

            <div className="bg-[#0e2727]">
                <JournalNav sections={sections} />

                <HeroSection hero={hero} trending={trending} latest={latest} featuredAuthor={featuredAuthor} />

                <AIFeatureBanner />

                {topSections.map((section, i) =>
                    i === 0 ? (
                        <SectionMagazine key={section.tag} section={section} />
                    ) : (
                        <SectionStrip
                            key={section.tag}
                            section={section}
                            theme={i % 2 === 0 ? 'ivory' : 'teal'}
                            variant={variantForIndex(i)}
                            showLead={i !== 1}
                        />
                    ),
                )}

                {hasEditorPicks && <EditorsPicks guides={editorPicks} />}

                <QuoteInterstitial />

                {bottomSections.map((section, i) => (
                    <SectionCompact key={section.tag} section={section} theme={i === 1 ? 'ivory' : themeForIndex(i + topSections.length)} />
                ))}

                <ClosingInterstitial />
            </div>
        </PublicLayout>
    );
}
